import store from '@/store'
import Constants from '@/utils/constants'
import DataService from '@/services/StrapiService';


/**
 * Creates a deep copy of an object so that no
 * "passed by reference side effects shit" happens
 * @param { Object } originalObject 
 * @returns 
 */
function deepCopyObject(originalObject) {
  return JSON.parse(JSON.stringify(originalObject))
}
/**
 * Fills an object with a deep value and creates every sub-object needed
 * @param {*} obj 
 * @param {*} keyPath ["attributes", "land", "data", "id"], 
 * @param {*} value 0 
 * -> returns {attributes: {land: {data: {id: 0}}
 */
function assignObjectFromPath(obj, keyPath, value) {
  var lastKeyIndex = keyPath.length - 1;
  for (var i = 0; i < lastKeyIndex; ++i) {
    var key = keyPath[i];
    if (!(key in obj)) {
      obj[key] = {}
    }
    obj = obj[key];
  }
  obj[keyPath[lastKeyIndex]] = value;
}

function getVenueCurrency(venue) {
  return (venue.attributes.currency.data != null) ? venue.attributes.currency.data.attributes.symbol : '€'
}

function getGrossPrice(netPrice, blank = false) {
  const taxRate = store.getters.getVenue.attributes.taxRate.data.attributes.taxRate
  const grossPrice = Number(netPrice) + (Number(netPrice) * (taxRate / 100))
  if (blank) {
    return Number(grossPrice).toFixed(2)
  } else {
    return Number(grossPrice).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
}

function getNetPrice(grossPrice, blank = false) {
  const taxRate = store.getters.getVenue.attributes.taxRate.data.attributes.taxRate
  let netPrice = Number(grossPrice) / (1 + (taxRate / 100))
  if (blank) {
    return Number(netPrice).toFixed(2)
  } else {
    return Number(netPrice).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
}

function getWeq(purchasePrice, sellingPrice) {
  const ek = Number(purchasePrice)
  const vk = getNetPrice(sellingPrice, true)
  var weq = 0
  if (ek > 0 && vk > 0) {
    weq = ek / vk * 100
  }
  return weq.toFixed(2)
}

function getWineSupplierText(generalText, supplier, venueSupplierOptions) {
  var text = generalText
  if (supplier) {
    var wineSupplier = venueSupplierOptions.find(option => option.value == supplier.id)
    if (wineSupplier) {
      text = wineSupplier.text
    } else if (supplier.attributes != null && supplier.attributes.title != null) {
      text = supplier.attributes.title
    } else if (supplier.title != null) {
      // The supplier can also be a plain { id, title } object (e.g. when
      // prefilled from a sheet row)
      text = supplier.title
    }
  }
  return text
}

function getNotDeletedPurchasePrices(purchasePrices) {
  var filteredPrices = []
  if (purchasePrices) {
    filteredPrices = purchasePrices.filter(price => price.wineSupplier.data && !shouldDeletePurchasePrice(price));
  }
  return filteredPrices;
}

function getNotDeletedRatings(ratings) {
  var filteredRatings = []
  if (ratings) {
    filteredRatings = ratings.filter(rating => rating.ratingAgency.data && !shouldDeleteRating(rating));
  }
  return filteredRatings;
}

/**
 * Check if purchasePrice should be deleted
 */
function shouldDeletePurchasePrice(purchasePrice) {
  if (Constants.SHOULD_DELETE in purchasePrice) {
    return true;
  }
  else {
    return false;
  }
}

function shouldDeleteRating(rating) {
  if (Constants.SHOULD_DELETE in rating) {
    return true;
  }
  else {
    return false;
  }
}

function equalsIgnoreCase(a, b) {
  return typeof a === 'string' && typeof b === 'string'
    ? a.localeCompare(b, undefined, { sensitivity: 'base' }) === 0
    : a === b;
}

function loadVenueSupplierOptions(venue) {
  var venueSupplierOptions = []
  venue.attributes.wineSuppliers.data.forEach((supplier) => {
    var option = {
      value: supplier.id,
      text: supplier.attributes.title
    }
    venueSupplierOptions.push(option)
  })
  return venueSupplierOptions
}

/**
 * Normalizes a venue object fetched from the API so it can be stored in the
 * Vuex store / localStorage. Ensures optional fields have sane defaults.
 * @param { Object } venueData - the venue object from DataService.getVenueDetail
 * @returns { Object } the normalized venue object
 */
function normalizeVenueData(venueData) {
  if (venueData.attributes.glassAmountPerBottle == null) {
    venueData.attributes.glassAmountPerBottle = []
  }
  for (var i = 0; i < venueData.attributes.wineSuppliers.data.length; i++) {
    if (venueData.attributes.wineSuppliers.data[i].attributes.address == null) {
      venueData.attributes.wineSuppliers.data[i].attributes.address = {
        street: null,
        city: null,
        zip: null,
        name: null
      }
    }
  }
  return venueData
}

/**
 * Find the default venue supplier option, which is the one containing the word "Sammellieferant"
 * @param {*} venue 
 * @returns 
 */
function getDefaultVenueSupplierOption(venue) {
  var defaultOption = null;
  const venueSupplierOptions = loadVenueSupplierOptions(venue);
  defaultOption = venueSupplierOptions.find(option => option.text.toLowerCase().includes("sammellieferant"));
  return defaultOption
}

/**
 * used for ek parsing during import
 * transforms "1.000,00 €" and "1000€" and "1.000" to 1000
 *  "1.456,78" or "1456.78" or "1.456,78 €"  to 1456.78
 */
function parsePotentiallyGroupedFloat(stringValue) {
  // if it's already a number, return it
  if (!isNaN(stringValue)) {
    return parseFloat(stringValue)
  }
  if (stringValue == null || stringValue == undefined || typeof stringValue !== 'string') {
    return 0;
  }
  
  // remove currency symbol:
  stringValue = stringValue.replace("€", "");
  stringValue = stringValue.trim();
  //  when comma,cut string with 2 digits after dot
  let parts = stringValue.split(',');
  if (parts.length > 1) {
    parts[1] = parts[1].substring(0, 2);
    stringValue = parts.join(',');
  }
  // remove all non-numeric characters:
  var result = stringValue.replace(/[^0-9]/g, '');

  if (/[,.]\d{1}$/.test(stringValue)) {
    // then add a dot as decimal separator
    result = result.replace(/(\d{1})$/, '.$1');
  }
  // check if we have a comma or dot as decimal separator
  if (/[,.]\d{2}$/.test(stringValue)) {
    // then add a dot as decimal separator
    result = result.replace(/(\d{2})$/, '.$1');
  }
  return parseFloat(result);
}

/**
 * used for parsing long supplier or grapeVariety strings in Create New Wine Modal
 */
function limitString(length, str) {
  if (str.length <= length) {
    return str;
  }
  return str.slice(0, length) + '…';
}

function checkForNullEmptyOrNegative(value) {
  if (value === "" || value === null || value < 0) {
    return 0;
  } else {
    return value;
  }
}
function filterInventoryVenueWines(inventoryVenueWines) {
  const checkedWines = inventoryVenueWines.filter((wine) => wine.inventoryVenueWineState === Constants.inventory.CHECKED || wine.inventoryVenueWineState === Constants.inventory.TRANSFERRED);
  const uncheckedWines = inventoryVenueWines.filter((wine) => wine.inventoryVenueWineState !== Constants.inventory.CHECKED || wine.inventoryVenueWineState === Constants.inventory.TRANSFERRED_UNCHECKED);
  const incompleteWines = checkedWines.filter((wine) => (wine.actualAmountFloat === null) || wine.purchasePrice === null || wine.purchasePrice === 0);
  const completeWines = checkedWines.filter((wine) => wine.actualAmountFloat !== null && wine.shelf !== null && wine.shelf !== '' && wine.purchasePrice !== null && wine.purchasePrice !== 0);
  const divergentStock = checkedWines.filter((wine) => wine.actualAmountFloat !== wine.targetAmountFloat)
  const duplicateWines = inventoryVenueWines.filter((wine) => wine.duplicate == true)

  return {
    checkedWines: checkedWines,
    uncheckedWines: uncheckedWines,
    incompleteWines: incompleteWines,
    completeWines: completeWines,
    divergentStock: divergentStock,
    allWines: inventoryVenueWines,
    duplicateWines: duplicateWines
  }
}
function processVenueWineIds(venueWineIds, inventoryId, loadInventory, $route, $store, $t) {
  venueWineIds.forEach(venueWineId => {
    DataService.getVenueWineDetail(venueWineId)
      .then(response => {
        const venueWine = response.data.data;
        const inventoryVenueWine = {
          venue_wine: venueWine.id,
          actualAmountFloat: venueWine.attributes.amountFloat,
          targetAmountFloat: 0,
          sellingPrice: venueWine.attributes.sellingPrice,
          shelf: venueWine.attributes.shelfNumber,
          year: venueWine.attributes.year,
          bottleSize: venueWine.attributes.bottleSize?.data?.id,
          inventoryVenueWineState: Constants.inventory.CHECKED,
          inventory: inventoryId
        }
        DataService.createInventoryVenueWine(inventoryVenueWine)
          .then(() => {
            loadInventory($route.params.id, $route.params.filterString);
            const alertobject = {
              text: $t('inventory.addedWines'),
              variant: 'success'
            }
            $store.commit('setAlertText', alertobject)
          })
          .catch(e => {
            console.log(e);
            const alertobject = {
              text: $t('inventory.errorWines'),
              variant: 'danger'
            }
            $store.commit('setAlertText', alertobject)
          });
      })
      .catch(e => {
        console.log(e);
      });
  });
}
function isInventoryOnly(role) {
  return role?.name == 'InventoryOnly'
}
function getKeysByValue(object, value) { return Object.keys(object).filter(key => object[key] === value); }

function reduceText(text, count, insertDots){
  return text.slice(0, count) + (((text.length > count) && insertDots) ? "..." : "");
}
function isInt(n){
  return Number(n) === n && n % 1 === 0;
}
function isFloat(n){
  return Number(n) === n && n % 1 !== 0;
}

const ADD_WINE_DRAFT_KEY = 'addWineDraft'

/**
 * Save the current add-wine dialog state to localStorage
 * @param {Object} selectedWinery
 * @param {Array} selectedWines
 * @param {Number} step
 * @param {Object|null} [sheetRow] The sheet row the dialog was opened with (if any)
 * @param {string|null} [sheetType] The sheet type ('inbound'/'outbound'), if any
 */
function saveAddWineDraft(selectedWinery, selectedWines, step, sheetRow = null, sheetType = null) {
  const draft = {
    selectedWinery: sanitizeForStorage(selectedWinery),
    selectedWines: selectedWines.map(wine => sanitizeForStorage(wine)),
    step: step,
    sheetRow: sanitizeForStorage(sheetRow),
    sheetType: sheetType,
    savedAt: new Date().toISOString()
  }
  try {
    localStorage.setItem(ADD_WINE_DRAFT_KEY, JSON.stringify(draft))
  } catch (e) {
    console.error('Failed to save add-wine draft:', e)
  }
}

/**
 * Load a saved add-wine dialog draft from localStorage
 * The draft includes the sheetRow and sheetType the dialog was opened with (if any).
 * @returns {Object|null} The draft or null if none exists
 */
function loadAddWineDraft() {
  try {
    const draft = localStorage.getItem(ADD_WINE_DRAFT_KEY)
    return draft ? JSON.parse(draft) : null
  } catch (e) {
    console.error('Failed to load add-wine draft:', e)
    return null
  }
}

/**
 * Remove the saved add-wine dialog draft from localStorage
 */
function clearAddWineDraft() {
  try {
    localStorage.removeItem(ADD_WINE_DRAFT_KEY)
  } catch (e) {
    console.error('Failed to clear add-wine draft:', e)
  }
}

/**
 * Remove properties that are not JSON-safe or not needed for storage
 * (loading states, etc.)
 * @param {Object} obj
 * @returns {Object} A clean copy safe for localStorage
 */
function sanitizeForStorage(obj) {
  if (obj == null || typeof obj !== 'object') {
    return obj
  }
  // Use deepCopyObject to break references, then remove transient props
  const clean = deepCopyObject(obj)
  delete clean.loading
  return clean
}

export {
  parsePotentiallyGroupedFloat,
  deepCopyObject,
  assignObjectFromPath,
  getVenueCurrency,
  getNetPrice,
  getGrossPrice,
  getWeq,
  getWineSupplierText,
  getNotDeletedPurchasePrices,
  getNotDeletedRatings,
  shouldDeletePurchasePrice,
  shouldDeleteRating,
  equalsIgnoreCase,
  loadVenueSupplierOptions,
  normalizeVenueData,
  getDefaultVenueSupplierOption,
  limitString,
  checkForNullEmptyOrNegative,
  filterInventoryVenueWines,
  getKeysByValue,
  isInventoryOnly,
  processVenueWineIds,
  reduceText,
  isInt,
	isFloat,
  saveAddWineDraft,
  loadAddWineDraft,
  clearAddWineDraft,
}